import Clarity from '@microsoft/clarity';

/**
 * Microsoft Clarity tracking.
 *
 * There is deliberately NO page-view effect here. The one that used to live in this hook called
 * `Clarity.identify(undefined, undefined, location.pathname)` to attach a custom page id — but
 * `identify`'s first argument is the visitor's custom-id and Clarity requires it, so that call
 * could never do its stated job. Clearing its type error by passing a literal (`'anonymous'`) made
 * it worse rather than better: it fired on every route change for every visitor, so it stamped one
 * shared identity on everybody and overwrote the real per-user id `App.tsx` sets on login — the
 * last writer on each navigation decided who Clarity thought the visitor was.
 *
 * Clarity records page URLs on its own, so nothing is lost by not asserting one. `identifyUser` is
 * the single writer of identity; see useClarityTracking.test.tsx.
 */
export const useClarityTracking = () => {
  // Utility functions for custom event tracking
  const trackEvent = (eventName: string) => {
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const isDevelopment = import.meta.env.DEV;

    if (clarityProjectId && (!isDevelopment || import.meta.env.VITE_CLARITY_ENABLE_DEV === 'true')) {
      try {
        Clarity.event(eventName);
        console.log('Clarity event tracked:', eventName);
      } catch (error) {
        console.warn('Failed to track event with Clarity:', error);
      }
    }
  };

  const setTag = (key: string, value: string | string[]) => {
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const isDevelopment = import.meta.env.DEV;

    if (clarityProjectId && (!isDevelopment || import.meta.env.VITE_CLARITY_ENABLE_DEV === 'true')) {
      try {
        Clarity.setTag(key, value);
        console.log('Clarity tag set:', key, value);
      } catch (error) {
        console.warn('Failed to set tag with Clarity:', error);
      }
    }
  };

  const identifyUser = (customId: string, friendlyName?: string) => {
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const isDevelopment = import.meta.env.DEV;

    if (clarityProjectId && (!isDevelopment || import.meta.env.VITE_CLARITY_ENABLE_DEV === 'true')) {
      try {
        Clarity.identify(customId, undefined, undefined, friendlyName);
        console.log('Clarity user identified:', customId, friendlyName);
      } catch (error) {
        console.warn('Failed to identify user with Clarity:', error);
      }
    }
  };

  return {
    trackEvent,
    setTag,
    identifyUser,
  };
};